import { ClientOnly } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import type {
	EmbedUmbBlockElementModel,
	EmbedUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";

type EmbedBlockProps = BlockItemProps & {
	content: EmbedUmbBlockElementModel;
	settings: EmbedUmbBlockSettingsElementModel;
};

export default function EmbedUmbBlock({
	className,
	content,
	settings,
}: EmbedBlockProps) {
	if (!content.properties?.embedCode) return null;

	return (
		<ClientOnly>
			<EmbedBlock content={content} settings={settings} className={className} />
		</ClientOnly>
	);
}

function EmbedBlock({ className, content, settings }: EmbedBlockProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const renderedEmbedCodeRef = useRef<string | null>(null);
	const embedCode = content.properties?.embedCode;

	useEffect(() => {
		const container = containerRef.current;
		if (
			!container ||
			!embedCode ||
			renderedEmbedCodeRef.current === embedCode
		) {
			return;
		}

		container.innerHTML = embedCode;

		for (const inertScript of container.querySelectorAll(
			"script:not([data-umbrella-embed-executed])",
		)) {
			const script = document.createElement("script");

			for (const attribute of inertScript.attributes) {
				script.setAttribute(attribute.name, attribute.value);
			}

			if (!inertScript.hasAttribute("async")) {
				script.async = false;
			}

			script.dataset.umbrellaEmbedExecuted = "";
			script.textContent = inertScript.textContent;
			inertScript.replaceWith(script);
		}

		renderedEmbedCodeRef.current = embedCode;
	}, [embedCode]);

	if (!embedCode) return null;

	return (
		<div
			ref={containerRef}
			id={settings?.properties?.generatedId ?? undefined}
			className={className}
		/>
	);
}
